// The HTTP implementation of `ApiClient`, over a `Transport`.
//
// Everything the app reads or writes passes through here: one base URL, one
// bearer token, one error shape. The client never composes a filter
// expression — every query parameter is named and typed (api-contract §0.5),
// which was both the coupling and the injection surface in the old data layer.
//
// THE TRANSPORT is the one seam. `client.ts` hands this class the real one —
// the runtime config's base URL, the Entra tokens, the browser's `fetch`. The
// local harness (`app/mock/client.ts`) hands it one whose `fetch` calls the
// backend's own route handlers in-process, so the harness exercises this file
// too, money conversion and all.

import { ApiError, type ApiErrorBody } from './errors'
import { priceFromWire, quantityWire } from '../lib/format'
import { ANALYTICS_LENS_HEADER } from '../lib/constants'
import { currentAnalyticsLens, lensApplies } from './lens'
import type { ApiClient } from './contract'
import { INVOICE_NUMERIC_FIELDS, LINE_ITEM_NUMERIC_FIELDS } from './types'
import type {
  AnalyticsByDimension,
  AnalyticsCorrections,
  AnalyticsKpis,
  AnalyticsParams,
  AnalyticsSpendByCode,
  AnalyticsSpendByEntity,
  Assignment,
  AssignmentListParams,
  AssignmentRequest,
  AssignmentResult,
  AssociatedEntity,
  InvoiceLineItem,
  Company,
  CompanyEmailDomain,
  IntakeSender,
  SenderFormatDefault,
  InAppUploadBlock,
  SenderRule,
  SenderRulePut,
  Conversation,
  DecisionBatchRequest,
  DecisionBatchResult,
  DecisionRequest,
  DecisionResult,
  DirectoryUser,
  ExportDetail,
  ExportResult,
  FiscalPick,
  Flag,
  Hold,
  IntakeSaveRequest,
  RoutingPreview,
  RoutingPreviewRequest,
  IntakeSaveResult,
  Invoice,
  InvoiceBundle,
  InvoiceInclude,
  InvoiceListParams,
  Page,
  PresignRequest,
  PresignResult,
  Profile,
  QueueStuck,
  ReferenceListParams,
  RegisterUploadRequest,
  ReminderSubscription,
  ReminderWrite,
  SignedUrl,
  Upload,
  UserRole,
  UserRoleToken,
  Vehicle,
  VehicleImportPreview,
  VehicleImportResult,
  Vendor,
  VendorAlias,
  CompanyAlias,
  Code,
  CodingRule,
  GroupingRule,
  GroupingRuleCreateRequest,
  GroupingRulePatchRequest,
  LineGroup,
  LineGroupsRequest,
  LineGroupsResult,
  VendorExtractionGuidance,
  VendorImportPreview,
  VendorImportResult,
  ViewSessionInput,
  ViewSessionsResult,
  Department,
  Account,
  InactiveCascade,
  MarketingCategory,
  CreateReportResponse,
  ReportCriteria,
  ReportContentsResponse,
  ReportRequestSummary,
  DeployReport,
  OpsErrorKind,
  OpsErrorsResponse,
  OpsErrorsWindow,
  OversightRule,
  OversightRuleConditionWrite,
  RequiredApprovals,
  UploadNotificationRecipient,
  Notification,
  NotificationSummary,
  UploadNotificationRecipientCreate,
  DecisionSource,
} from './types'

// ---------------------------------------------------------------------------
// Money: decimal string on the wire, number in the app (see types.ts).
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>

// The header's decimal-string fields, read as numbers and written back as
// two-place strings. The list lives beside the type it shapes (`types.ts`).
const INVOICE_MONEY = INVOICE_NUMERIC_FIELDS
// Decimal-string fields on a line item read as numbers. `quantity` is here for
// the READ side only; it is NOT money (`numeric(14,4)`) and is written at four
// places. `unit_price` is NOT here: it stays the wire's exact text (D-249), so
// $0.0049 never passes through a float (code review D-02).
const LINE_ITEM_MONEY = LINE_ITEM_NUMERIC_FIELDS
const LINE_ITEM_MONEY_WRITE = ['total'] as const
// The unit price is written exactly, every decimal kept, so $0.0049 reaches the
// server as "0.0049" and not "0.00".
const LINE_ITEM_PRICE = ['unit_price'] as const
const LINE_ITEM_QUANTITY = ['quantity'] as const

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** Two-decimal decimal string; the API rejects a JSON number for money. */
function toMoneyString(value: unknown): string | null {
  const n = toNumber(value)
  return n === null ? null : n.toFixed(2)
}

function parseMoneyFields<T>(row: unknown, fields: readonly string[]): T {
  if (!row || typeof row !== 'object') return row as T
  const out = { ...(row as Row) }
  for (const f of fields) if (f in out) out[f] = toNumber(out[f])
  return out as T
}

function parseInvoice(row: unknown): Invoice {
  return parseMoneyFields<Invoice>(row, INVOICE_MONEY)
}

// The two detail objects carry no numeric fields since D-190 (2026-09-28): a
// category id, its text and meaning, and a row version, all passed through.
function parseLineItem(row: unknown): InvoiceLineItem {
  return parseMoneyFields<InvoiceLineItem>(row, LINE_ITEM_MONEY)
}

/** Writes go the other way: numbers become decimal strings. */
function writeMoneyFields(body: Row, fields: readonly string[]): Row {
  const out = { ...body }
  for (const f of fields) {
    if (f in out && out[f] !== null && out[f] !== undefined) out[f] = toMoneyString(out[f])
    // an explicit null stays null — that is a field clear (§0.7)
  }
  return out
}

/** `numeric(14,4)` as a decimal string, e.g. `"2.5000"` — four places, because
    quantity is not money and two would round a fractional hour or gallon away. */
function toQuantityString(value: unknown): string | null {
  // Exact, never `toFixed(4)` — a float rounding (`quantityWire`).
  if (typeof value === 'number' || typeof value === 'string') return quantityWire(value)
  return null
}

/** A line item write: money at two decimals, `quantity` at four, and the unit
    price exactly (`priceFromWire`). Exported so the wire format is pinned by a
    test rather than by reading this file. */
export function writeLineItemFields(body: Row): Row {
  const out = writeMoneyFields(body, LINE_ITEM_MONEY_WRITE)
  for (const f of LINE_ITEM_PRICE) {
    if (f in out && out[f] !== null && out[f] !== undefined) {
      const text = priceFromWire(out[f] as number | string)
      out[f] = text === '' ? null : text
    }
  }
  for (const f of LINE_ITEM_QUANTITY) {
    if (f in out && out[f] !== null && out[f] !== undefined) out[f] = toQuantityString(out[f])
  }
  return out
}

/** Money inside an aggregate row: the shapes differ per endpoint, so the field
    is named rather than inferred. */
function parseTotals<T>(rows: T[] | undefined, field = 'total'): T[] {
  return (rows ?? []).map((r) => ({ ...(r as object), [field]: toNumber((r as Row)[field]) }) as T)
}

// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------

type QueryValue = string | number | boolean | undefined | null | (string | number)[]

function query(params: Record<string, QueryValue> = {}): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      for (const v of value) sp.append(key, String(v))
    } else {
      sp.set(key, String(value))
    }
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** Where requests go and with what credentials. */
/**
 * S3's own one-line reason for refusing a presigned POST, from its XML error body
 * (`<Message>Access Denied</Message>`, `<Message>Invalid according to Policy: Policy
 * expired.</Message>`), or '' when there is none. It is what tells a key or
 * permission problem from an expired form. S3 writes the text, so it is kept to
 * one line and capped in length.
 */
function s3Reason(body: string): string {
  const message = /<Message>([^<]*)<\/Message>/.exec(body)?.[1] ?? /<Code>([^<]*)<\/Code>/.exec(body)?.[1] ?? ''
  return message.replace(/\s+/g, ' ').trim().slice(0, 200)
}

export interface Transport {
  /** The API base URL, `/api/v1` included. */
  apiBaseUrl(): string
  /** A bearer token for the API. */
  apiToken(): Promise<string>
  /** A bearer token for Microsoft Graph (the directory search). */
  graphToken(): Promise<string>
  fetch(url: string, init: RequestInit): Promise<Response>
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Multipart body; skips the JSON content type. */
  form?: FormData
  idempotencyKey?: string
  /** Absolute URL (Graph); skips the API base and the API token. */
  absolute?: string
  /** Graph's token rather than the API's. */
  graph?: boolean
  expectEmpty?: boolean
  /**
   * Let the request outlive the page (`fetch`'s `keepalive`).
   *
   * Only the view-session flush uses it. A normal fetch started while the tab
   * is closing is cancelled with the document, which is exactly when the last
   * measurement of a session is sent. Bodies are capped at 64 KB by the
   * standard, which a handful of session rows is nowhere near.
   */
  keepalive?: boolean
}

export class HttpApiClient implements ApiClient {
  private readonly transport: Transport

  constructor(transport: Transport) {
    this.transport = transport
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = options.absolute ?? `${this.transport.apiBaseUrl().replace(/\/$/, '')}${path}`
    const token = await (options.graph === true ? this.transport.graphToken() : this.transport.apiToken())
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    }
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
    // The Analytics tab on screen, on a read of this API only (D-258; lens.ts).
    const lens = currentAnalyticsLens()
    if (lens !== null && options.absolute === undefined && lensApplies(options.method ?? 'GET', path)) {
      headers[ANALYTICS_LENS_HEADER] = lens
    }
    let body: BodyInit | undefined
    if (options.form) {
      body = options.form
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(options.body)
    }

    const res = await this.transport.fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body,
      ...(options.keepalive === true ? { keepalive: true } : {}),
    })

    if (!res.ok) {
      let parsed: ApiErrorBody = { code: `http_${res.status}`, message: res.statusText }
      try {
        const json = (await res.json()) as { error?: ApiErrorBody }
        if (json?.error) parsed = json.error
      } catch {
        // 401 from the gateway has an empty body, by design.
        if (res.status === 401) parsed = { code: 'unauthenticated', message: 'Your session has expired. Sign in again.' }
      }
      throw new ApiError(res.status, parsed)
    }

    if (options.expectEmpty || res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  private newIdempotencyKey(): string {
    return crypto.randomUUID()
  }

  // --- session ------------------------------------------------------------

  getProfile(): Promise<Profile> {
    return this.request<Profile>('/me')
  }

  // --- queues -------------------------------------------------------------

  getStuckQueues(params: { workspace?: string } = {}): Promise<QueueStuck> {
    return this.request<QueueStuck>(`/queues/stuck${query({ workspace: params.workspace ?? 'invoice' })}`)
  }

  // --- invoices -----------------------------------------------------------

  async listInvoices(params: InvoiceListParams = {}): Promise<Page<Invoice>> {
    const page = await this.request<Page<Invoice>>(
      `/invoices${query({
        status: params.status,
        company_id: params.company_id,
        vendor_id: params.vendor_id,
        vendor_name: params.vendor_name,
        date_from: params.date_from,
        date_to: params.date_to,
        total_min: params.total_min === undefined ? undefined : toMoneyString(params.total_min),
        total_max: params.total_max === undefined ? undefined : toMoneyString(params.total_max),
        export_id: params.export_id,
        upload_id: params.upload_id,
        manual_entry: params.manual_entry,
        unresolved_flags: params.unresolved_flags,
        flag_type: params.flag_type,
        q: params.q,
        since: params.since,
        // The dashboard's Filters button (owner, 2026-09-14) — the same seven
        // the aggregates take, so the listing matches the panels above it.
        uploaded_from: params.uploaded_from,
        uploaded_to: params.uploaded_to,
        exported_from: params.exported_from,
        exported_to: params.exported_to,
        code_id: params.code_id,
        department_id: params.department_id,
        account_id: params.account_id,
        flag_type_any: params.flag_type_any,
        has_active_hold: params.has_active_hold,
        sort: params.sort,
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    )
    return { ...page, data: (page.data ?? []).map(parseInvoice) }
  }

  async getInvoice(invoiceId: string, include: InvoiceInclude[] = []): Promise<InvoiceBundle> {
    const bundle = await this.request<InvoiceBundle>(
      `/invoices/${encodeURIComponent(invoiceId)}${query({ include: include.length ? include.join(',') : undefined })}`,
    )
    return {
      ...bundle,
      invoice: parseInvoice(bundle.invoice),
      line_items: bundle.line_items?.map(parseLineItem),
    }
  }

  async routingPreview(invoiceId: string, body: RoutingPreviewRequest): Promise<RoutingPreview> {
    // The intake save's own money and line conversions, so the preview reads
    // the form exactly as a Release would send it.
    const payload = {
      invoice: body.invoice ? writeMoneyFields(body.invoice as Row, INVOICE_MONEY) : undefined,
      line_items: body.line_items
        ? {
            upsert: body.line_items.upsert?.map((c) => writeLineItemFields(c as unknown as Row)),
            remove: body.line_items.remove,
          }
        : undefined,
      line_groups: body.line_groups,
    }
    const result = await this.request<{ data: RoutingPreview }>(
      `/invoices/${encodeURIComponent(invoiceId)}/routing-preview`,
      { method: 'POST', body: payload },
    )
    return result.data
  }

  async intakeSave(invoiceId: string, body: IntakeSaveRequest): Promise<IntakeSaveResult> {
    const payload = {
      ...body,
      invoice: writeMoneyFields(body.invoice as Row, INVOICE_MONEY),
      line_items: body.line_items
        ? {
            upsert: body.line_items.upsert?.map((c) => writeLineItemFields(c as unknown as Row)),
            remove: body.line_items.remove,
          }
        : undefined,
    }
    const result = await this.request<IntakeSaveResult>(
      `/invoices/${encodeURIComponent(invoiceId)}/intake-save`,
      { method: 'POST', body: payload },
    )
    return {
      ...result,
      invoice: parseInvoice(result.invoice),
      line_items: result.line_items?.map(parseLineItem),
    }
  }

  // --- line items ------------------------------------------------------------

  async changeLineGroups(invoiceId: string, body: LineGroupsRequest): Promise<LineGroupsResult> {
    const result = await this.request<LineGroupsResult>(`/invoices/${encodeURIComponent(invoiceId)}/line-groups`, {
      method: 'POST',
      body,
    })
    return { ...result, invoice: parseInvoice(result.invoice), line_items: result.line_items.map(parseLineItem) }
  }

  async listLineItems(invoiceId: string): Promise<InvoiceLineItem[]> {
    const res = await this.request<{ data: InvoiceLineItem[] }>(`/invoices/${encodeURIComponent(invoiceId)}/line-items`)
    return (res.data ?? []).map(parseLineItem)
  }

  async listLineItemsForInvoices(invoiceIds: string[]): Promise<InvoiceLineItem[]> {
    if (invoiceIds.length === 0) return []
    const out: InvoiceLineItem[] = []
    // The API caps a multi-id read at 200 ids (§0.4).
    for (let i = 0; i < invoiceIds.length; i += 200) {
      const slice = invoiceIds.slice(i, i + 200)
      const res = await this.request<{ data: InvoiceLineItem[] }>(`/line-items${query({ invoice_ids: slice.join(',') })}`)
      out.push(...(res.data ?? []).map(parseLineItem))
    }
    return out
  }

  async setLineItemAccount(
    invoiceId: string,
    lineItemId: string,
    body: { account_id: string | null; row_version?: number },
  ): Promise<{ line_item: InvoiceLineItem; correction_id?: string | null }> {
    const res = await this.request<{ line_item: InvoiceLineItem; correction_id?: string | null }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}/account`,
      { method: 'PATCH', body },
    )
    return { ...res, line_item: parseLineItem(res.line_item) }
  }
  async setLineItemDepartment(
    invoiceId: string,
    lineItemId: string,
    body: { department_id: string | null; row_version?: number },
  ): Promise<{ line_item: InvoiceLineItem; correction_id?: string | null }> {
    const res = await this.request<{ line_item: InvoiceLineItem; correction_id?: string | null }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}/department`,
      { method: 'PATCH', body },
    )
    return { ...res, line_item: parseLineItem(res.line_item) }
  }
  async setLineItemMarketingDetail(
    invoiceId: string,
    lineItemId: string,
    body: { category_id: string | null; row_version: number },
  ): Promise<{ line_item: InvoiceLineItem; correction_ids: string[] }> {
    const res = await this.request<{ line_item: unknown; correction_ids?: string[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}/marketing-detail`,
      { method: 'PATCH', body },
    )
    return { line_item: parseLineItem(res.line_item), correction_ids: res.correction_ids ?? [] }
  }


  // --- flags --------------------------------------------------------------

  resolveFlag(invoiceId: string, flagId: string): Promise<{ flag: Flag; unresolved_flag_count: number }> {
    return this.request(`/invoices/${encodeURIComponent(invoiceId)}/flags/${encodeURIComponent(flagId)}/resolve`, {
      method: 'POST',
      body: {},
    })
  }

  // --- decisions ----------------------------------------------------------

  async createDecision(body: DecisionRequest): Promise<DecisionResult> {
    const result = await this.request<DecisionResult>('/decisions', {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
    return { ...result, invoice: parseInvoice(result.invoice) }
  }

  createDecisionBatch(body: DecisionBatchRequest): Promise<DecisionBatchResult> {
    return this.request('/decisions/batch', {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
  }

  /**
   * §21.1 The handling-time flush. `keepalive` so a tab closing does not cancel
   * it, and no idempotency key: the natural key is `client_session_id`, which
   * the server dedupes on, so a repeat is free without one.
   */
  recordViewSessions(sessions: ViewSessionInput[]): Promise<ViewSessionsResult> {
    return this.request('/view-sessions', {
      method: 'POST',
      body: { sessions },
      keepalive: true,
    })
  }

  // --- holds --------------------------------------------------------------

  async createHold(invoiceId: string, body: { reason_category: string; reason?: string }): Promise<Hold> {
    const res = await this.request<{ hold: Hold }>(`/invoices/${encodeURIComponent(invoiceId)}/holds`, {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
    return res.hold
  }

  async releaseHold(holdId: string): Promise<Hold> {
    const res = await this.request<{ hold: Hold }>(`/holds/${encodeURIComponent(holdId)}/release`, { method: 'POST', body: {} })
    return res.hold
  }

  // --- conversations ------------------------------------------------------

  async listConversations(invoiceId: string): Promise<Conversation[]> {
    const res = await this.request<{ data: Conversation[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/conversations`,
    )
    return res.data ?? []
  }

  createConversation(
    invoiceId: string,
    body: { body: string; mentions: string[] },
  ): Promise<{ conversation: Conversation; notifications_queued: number }> {
    return this.request(`/invoices/${encodeURIComponent(invoiceId)}/conversations`, { method: 'POST', body })
  }

  // --- associated entities ------------------------------------------------

  async listAssociatedEntities(invoiceId: string): Promise<AssociatedEntity[]> {
    const res = await this.request<{ data: AssociatedEntity[] }>(
      `/invoices/${encodeURIComponent(invoiceId)}/associated-entities`,
    )
    return res.data ?? []
  }

  async createAssociatedEntity(invoiceId: string, body: Partial<AssociatedEntity>): Promise<AssociatedEntity> {
    const res = await this.request<{ associated_entity: AssociatedEntity }>(
      `/invoices/${encodeURIComponent(invoiceId)}/associated-entities`,
      { method: 'POST', body },
    )
    return res.associated_entity
  }

  async updateAssociatedEntity(entityId: string, body: Partial<AssociatedEntity>): Promise<AssociatedEntity> {
    const res = await this.request<{ associated_entity: AssociatedEntity }>(
      `/associated-entities/${encodeURIComponent(entityId)}`,
      { method: 'PATCH', body },
    )
    return res.associated_entity
  }

  deleteAssociatedEntity(entityId: string, rowVersion: number): Promise<void> {
    return this.request<void>(
      `/associated-entities/${encodeURIComponent(entityId)}${query({ row_version: rowVersion })}`,
      { method: 'DELETE', expectEmpty: true },
    )
  }

  // --- uploads ------------------------------------------------------------

  presignUpload(body: PresignRequest): Promise<PresignResult> {
    return this.request('/uploads/presign', { method: 'POST', body })
  }

  async putUploadFile(presigned: PresignResult, file: File): Promise<void> {
    const form = new FormData()
    for (const [k, v] of Object.entries(presigned.fields ?? {})) form.append(k, v)
    form.append('file', file)
    // A presigned S3 POST is not an API call: no bearer token, no base URL.
    const res = await this.transport.fetch(presigned.url, { method: 'POST', body: form })
    if (!res.ok) {
      const reason = s3Reason(await res.text().catch(() => ''))
      throw new ApiError(res.status, {
        code: 'upload_failed',
        message: `The file could not be stored (HTTP ${res.status}${reason ? `: ${reason}` : ''}).`,
      })
    }
  }

  async registerUpload(body: RegisterUploadRequest, idempotencyKey: string): Promise<Upload> {
    const res = await this.request<{ upload: Upload }>('/uploads/register', {
      method: 'POST',
      body,
      idempotencyKey,
    })
    return res.upload
  }

  async getUpload(uploadId: string): Promise<Upload> {
    const res = await this.request<{ upload: Upload }>(`/uploads/${encodeURIComponent(uploadId)}`)
    return res.upload
  }

  getUploadPdfUrl(uploadId: string): Promise<SignedUrl> {
    return this.request(`/uploads/${encodeURIComponent(uploadId)}/pdf-url`)
  }

  // --- exports ------------------------------------------------------------

  createExport(invoiceIds: string[], pick: FiscalPick, idempotencyKey?: string, source?: DecisionSource): Promise<ExportResult> {
    return this.request('/exports', {
      method: 'POST',
      body: {
        invoice_ids: invoiceIds,
        fiscal_year: pick.fiscalYear,
        fiscal_period: pick.fiscalPeriod,
        ...(source === undefined ? {} : { decision_source: source }),
      },
      idempotencyKey: idempotencyKey ?? this.newIdempotencyKey(),
    })
  }

  getExport(exportId: string): Promise<ExportDetail> {
    return this.request(`/exports/${encodeURIComponent(exportId)}`)
  }

  getExportFileUrl(exportId: string, fileId: string): Promise<SignedUrl> {
    return this.request(
      `/exports/${encodeURIComponent(exportId)}/files/${encodeURIComponent(fileId)}/download-url`,
    )
  }

  // --- assignments --------------------------------------------------------

  listAssignments(params: AssignmentListParams = {}): Promise<Page<Assignment>> {
    return this.request(
      `/assignments${query({
        workspace: params.workspace,
        role: params.role,
        company_id: params.company_id,
        active: params.active,
        user_id: params.user_id,
        sort: 'created_at_desc',
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    )
  }

  createAssignment(body: AssignmentRequest): Promise<AssignmentResult> {
    return this.request('/assignments', {
      method: 'POST',
      body,
      idempotencyKey: this.newIdempotencyKey(),
    })
  }

  // --- internal step roles ------------------------------------------------

  async listUserRoles(
    params: { workspace?: string; role?: UserRoleToken; user_id?: string } = {},
  ): Promise<UserRole[]> {
    const res = await this.request<{ data: UserRole[] }>(`/user-roles${query({ ...params })}`)
    return res.data ?? []
  }

  async createUserRole(body: {
    workspace: string
    role: UserRoleToken
    user_id: string
    user_upn: string
    user_name?: string | null
    user_email?: string | null
  }): Promise<UserRole> {
    const res = await this.request<{ user_role: UserRole }>('/user-roles', { method: 'POST', body })
    return res.user_role
  }

  deleteUserRole(userRoleId: string): Promise<void> {
    return this.request<void>(`/user-roles/${encodeURIComponent(userRoleId)}`, {
      method: 'DELETE',
      expectEmpty: true,
    })
  }

  // --- reminder subscriptions --------------------------------------------

  async listReminderSubscriptions(params: { all?: boolean } = {}): Promise<ReminderSubscription[]> {
    const res = await this.request<{ data: ReminderSubscription[] }>(
      `/reminder-subscriptions${query({ all: params.all ? 'true' : undefined })}`,
    )
    return res.data ?? []
  }

  async listReminderSelectableSteps(subscriberId?: string): Promise<string[]> {
    const res = await this.request<{ steps: string[] }>(
      `/reminder-subscriptions/selectable-steps${query({ subscriber_id: subscriberId })}`,
    )
    return res.steps ?? []
  }

  async createReminderSubscription(body: ReminderWrite): Promise<ReminderSubscription> {
    const res = await this.request<{ subscription: ReminderSubscription }>('/reminder-subscriptions', {
      method: 'POST',
      body,
    })
    return res.subscription
  }

  replaceReminderSubscription(
    id: string,
    body: ReminderWrite & { row_version: number },
  ): Promise<{ subscription: ReminderSubscription; superseded_id: string }> {
    return this.request(`/reminder-subscriptions/${encodeURIComponent(id)}`, { method: 'PUT', body })
  }

  async setReminderSubscriptionStatus(
    id: string,
    status: 'active' | 'paused' | 'cancelled',
  ): Promise<ReminderSubscription> {
    const res = await this.request<{ subscription: ReminderSubscription }>(
      `/reminder-subscriptions/${encodeURIComponent(id)}/status`,
      { method: 'PATCH', body: { status } },
    )
    return res.subscription
  }

  // --- reference data -----------------------------------------------------

  listCompanies(params: ReferenceListParams = {}): Promise<Page<Company>> {
    return this.request(`/companies${query({ ...params })}`)
  }

  async createCompany(body: { company_id: string; company_name: string }): Promise<Company> {
    const res = await this.request<{ company: Company }>('/companies', { method: 'POST', body })
    return res.company
  }

  async updateCompany(
    companyId: string,
    body: { company_name?: string; is_active?: boolean; row_version: number },
  ): Promise<Company> {
    const res = await this.request<{ company: Company }>(`/companies/${encodeURIComponent(companyId)}`, {
      method: 'PATCH',
      body,
    })
    return res.company
  }

  listCompanyEmailDomains(
    params: { company_id?: string; active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<CompanyEmailDomain>> {
    return this.request(`/company-email-domains${query({ ...params })}`)
  }

  listIntakeSenders(
    params: { active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<IntakeSender>> {
    return this.request(`/intake-senders${query({ ...params })}`)
  }

  listSenderFormatDefaults(
    params: { active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<SenderFormatDefault>> {
    return this.request(`/sender-format-defaults${query({ ...params })}`)
  }

  listInAppUploadBlocks(
    params: { active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<InAppUploadBlock>> {
    return this.request(`/in-app-upload-blocks${query({ ...params })}`)
  }

  putSenderRule(body: SenderRulePut): Promise<{ sender_rule: SenderRule; changed: boolean }> {
    return this.request('/sender-rules', { method: 'PUT', body })
  }

  listVendors(params: ReferenceListParams = {}): Promise<Page<Vendor>> {
    return this.request(`/vendors${query({ ...params })}`)
  }

  async createVendor(body: Partial<Vendor> & { vendor_id: string; vendor_name: string }): Promise<Vendor> {
    const res = await this.request<{ vendor: Vendor }>('/vendors', { method: 'POST', body })
    return res.vendor
  }

  async updateVendor(vendorId: string, body: Partial<Vendor> & { row_version: number }): Promise<Vendor> {
    const res = await this.request<{ vendor: Vendor }>(`/vendors/${encodeURIComponent(vendorId)}`, {
      method: 'PATCH',
      body,
    })
    return res.vendor
  }

  previewVendorImport(file: File): Promise<VendorImportPreview> {
    const form = new FormData()
    form.append('file', file)
    return this.request('/vendors/import/preview', { method: 'POST', form })
  }

  commitVendorImport(previewToken: string, idempotencyKey?: string): Promise<VendorImportResult> {
    return this.request('/vendors/import/commit', {
      method: 'POST',
      body: { preview_token: previewToken },
      idempotencyKey: idempotencyKey ?? this.newIdempotencyKey(),
    })
  }

  listCodes(params: ReferenceListParams = {}): Promise<Page<Code>> {
    return this.request(`/codes${query({ ...params })}`)
  }

  async createCode(body: {
    code_id: string
    code_name: string
    department_name?: string
    account_name?: string
  }): Promise<Code> {
    const res = await this.request<{ code: Code }>('/codes', { method: 'POST', body })
    return res.code
  }

  async updateCode(
    codeId: string,
    body: { code_name?: string; is_active?: boolean; row_version: number },
  ): Promise<Code> {
    const res = await this.request<{ code: Code }>(`/codes/${encodeURIComponent(codeId)}`, {
      method: 'PATCH',
      body,
    })
    return res.code
  }

  async listDepartments(
    params: ReferenceListParams & { account_id?: string } = {},
  ): Promise<Department[]> {
    const res = await this.request<{ data: Department[] }>(
      `/departments${query({ ...params })}`,
    )
    return res.data
  }

  async listAccounts(params: ReferenceListParams = {}): Promise<Account[]> {
    const res = await this.request<{ data: Account[] }>(
      `/accounts${query({ ...params })}`,
    )
    return res.data
  }

  async createDepartment(body: {
    department_id: string
    department_name: string
    search_terms?: string | null
  }): Promise<Department> {
    const res = await this.request<{ department: Department }>('/departments', {
      method: 'POST',
      body,
    })
    return res.department
  }

  async updateDepartment(
    departmentId: string,
    body: { department_name?: string; search_terms?: string | null; is_active?: boolean; row_version: number },
  ): Promise<Department> {
    const res = await this.request<{ department: Department }>(
      `/departments/${encodeURIComponent(departmentId)}`,
      { method: 'PATCH', body },
    )
    return res.department
  }

  async createAccount(body: {
    account_id: string
    account_name: string
    search_terms?: string | null
  }): Promise<Account> {
    const res = await this.request<{ account: Account }>('/accounts', {
      method: 'POST',
      body,
    })
    return res.account
  }

  async updateAccount(
    accountId: string,
    body: {
      account_name?: string
      search_terms?: string | null
      is_active?: boolean
      row_version: number
    },
  ): Promise<Account> {
    const res = await this.request<{ account: Account }>(
      `/accounts/${encodeURIComponent(accountId)}`,
      { method: 'PATCH', body },
    )
    return res.account
  }

  async previewInactive(kind: 'account' | 'department' | 'code', id: string): Promise<InactiveCascade> {
    const collection = kind === 'account' ? 'accounts' : kind === 'department' ? 'departments' : 'codes'
    const res = await this.request<{ cascade: InactiveCascade }>(
      `/${collection}/${encodeURIComponent(id)}/inactive-preview`,
    )
    return res.cascade
  }

  async listMarketingCategories(params: ReferenceListParams = {}): Promise<MarketingCategory[]> {
    const res = await this.request<{ data: MarketingCategory[] }>(
      `/marketing-categories${query({ ...params })}`,
    )
    return res.data ?? []
  }

  async createMarketingCategory(body: { category: string; context: string }): Promise<MarketingCategory> {
    const res = await this.request<{ marketing_category: MarketingCategory }>('/marketing-categories', {
      method: 'POST',
      body,
    })
    return res.marketing_category
  }

  async updateMarketingCategory(
    id: string,
    body: { category?: string; context?: string; is_active?: boolean; row_version: number },
  ): Promise<MarketingCategory> {
    const res = await this.request<{ marketing_category: MarketingCategory }>(
      `/marketing-categories/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.marketing_category
  }



  async listCodingRules(
    params: {
      vendor_id?: string
      account_id?: string
      department_id?: string
      active?: boolean
      limit?: number
      cursor?: string
    } = {},
  ): Promise<Page<CodingRule>> {
    return this.request(`/coding-rules${query({ ...params })}`)
  }

  async createCodingRule(body: {
    vendor_id: string
    description_scope?: string | null
    account_id?: string | null
    department_id?: string | null
  }): Promise<CodingRule> {
    const res = await this.request<{ coding_rule: CodingRule }>('/coding-rules', {
      method: 'POST',
      body,
    })
    return res.coding_rule
  }

  async updateCodingRule(
    id: string,
    body: {
      description_scope?: string | null
      account_id?: string | null
      department_id?: string | null
      is_active?: boolean
      row_version: number
    },
  ): Promise<CodingRule> {
    const res = await this.request<{ coding_rule: CodingRule }>(
      `/coding-rules/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.coding_rule
  }


  // --- Rules -> Grouping (D-402) -------------------------------------------------

  async listLineGroups(vendorId: string | null): Promise<LineGroup[]> {
    const res = await this.request<{ line_groups: LineGroup[] }>(
      `/line-groups${query(vendorId === null ? {} : { vendor_id: vendorId })}`,
    )
    return res.line_groups
  }

  listGroupingRules(
    params: { vendor_id?: string; active?: boolean; source?: 'Controller' | 'Learned'; limit?: number; cursor?: string } = {},
  ): Promise<Page<GroupingRule>> {
    return this.request(`/grouping-rules${query({ ...params })}`)
  }

  async createGroupingRule(body: GroupingRuleCreateRequest): Promise<GroupingRule> {
    const res = await this.request<{ grouping_rule: GroupingRule }>('/grouping-rules', { method: 'POST', body })
    return res.grouping_rule
  }

  async updateGroupingRule(id: string, body: GroupingRulePatchRequest): Promise<GroupingRule> {
    const res = await this.request<{ grouping_rule: GroupingRule }>(`/grouping-rules/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    })
    return res.grouping_rule
  }

  listOversightRules(
    params: { active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<OversightRule>> {
    return this.request(`/oversight-rules${query({ ...params })}`)
  }

  async createOversightRule(
    body: Partial<RequiredApprovals> & { conditions: OversightRuleConditionWrite[] },
  ): Promise<OversightRule> {
    const res = await this.request<{ oversight_rule: OversightRule }>('/oversight-rules', {
      method: 'POST',
      body,
    })
    return res.oversight_rule
  }

  async updateOversightRule(
    id: string,
    body: Partial<RequiredApprovals> & {
      conditions?: OversightRuleConditionWrite[]
      is_active?: boolean
      row_version: number
    },
  ): Promise<OversightRule> {
    const res = await this.request<{ oversight_rule: OversightRule }>(
      `/oversight-rules/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.oversight_rule
  }

  async listNotifications(): Promise<NotificationSummary[]> {
    const res = await this.request<{ data: NotificationSummary[] }>('/notifications')
    return res.data ?? []
  }

  async getNotification(id: string): Promise<Notification> {
    const res = await this.request<{ notification: Notification }>(`/notifications/${encodeURIComponent(id)}`)
    return res.notification
  }

  async listUploadNotificationRecipients(
    params: { active?: boolean } = {},
  ): Promise<UploadNotificationRecipient[]> {
    const res = await this.request<{ data: UploadNotificationRecipient[] }>(
      `/upload-notification-recipients${query({ ...params })}`,
    )
    return res.data ?? []
  }

  async createUploadNotificationRecipient(
    body: UploadNotificationRecipientCreate,
  ): Promise<UploadNotificationRecipient> {
    const res = await this.request<{ upload_notification_recipient: UploadNotificationRecipient }>(
      '/upload-notification-recipients',
      { method: 'POST', body },
    )
    return res.upload_notification_recipient
  }

  async updateUploadNotificationRecipient(
    id: string,
    body: { is_active: boolean; row_version: number },
  ): Promise<UploadNotificationRecipient> {
    const res = await this.request<{ upload_notification_recipient: UploadNotificationRecipient }>(
      `/upload-notification-recipients/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.upload_notification_recipient
  }

  listVendorAliases(
    params: { vendor_id?: string; active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<VendorAlias>> {
    return this.request(`/vendor-aliases${query({ ...params })}`)
  }

  async createVendorAlias(body: {
    alias_name: string
    vendor_id: string
    evidence?: string | null
  }): Promise<VendorAlias> {
    const res = await this.request<{ vendor_alias: VendorAlias }>('/vendor-aliases', {
      method: 'POST',
      body,
    })
    return res.vendor_alias
  }

  async updateVendorAlias(
    id: string,
    body: {
      alias_name?: string
      vendor_id?: string
      evidence?: string | null
      is_active?: boolean
      row_version: number
    },
  ): Promise<VendorAlias> {
    const res = await this.request<{ vendor_alias: VendorAlias }>(
      `/vendor-aliases/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.vendor_alias
  }

  listCompanyAliases(
    params: { company_id?: string; active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<CompanyAlias>> {
    return this.request(`/company-aliases${query({ ...params })}`)
  }

  async createCompanyAlias(body: {
    alias_name: string
    company_id: string
    evidence?: string | null
  }): Promise<CompanyAlias> {
    const res = await this.request<{ company_alias: CompanyAlias }>('/company-aliases', {
      method: 'POST',
      body,
    })
    return res.company_alias
  }

  async updateCompanyAlias(
    id: string,
    body: {
      alias_name?: string
      company_id?: string
      evidence?: string | null
      is_active?: boolean
      row_version: number
    },
  ): Promise<CompanyAlias> {
    const res = await this.request<{ company_alias: CompanyAlias }>(
      `/company-aliases/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.company_alias
  }

  listVendorExtractionGuidance(
    params: { q?: string; active?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<Page<VendorExtractionGuidance>> {
    return this.request(`/vendor-extraction-guidance${query({ ...params })}`)
  }

  async createVendorExtractionGuidance(body: {
    vendor_id: string
    instruction: string
  }): Promise<VendorExtractionGuidance> {
    const res = await this.request<{ vendor_extraction_guidance: VendorExtractionGuidance }>(
      '/vendor-extraction-guidance',
      { method: 'POST', body },
    )
    return res.vendor_extraction_guidance
  }

  async supersedeVendorExtractionGuidance(
    id: string,
    body: { instruction: string; row_version: number },
  ): Promise<VendorExtractionGuidance> {
    const res = await this.request<{ vendor_extraction_guidance: VendorExtractionGuidance }>(
      `/vendor-extraction-guidance/${encodeURIComponent(id)}/supersede`,
      { method: 'POST', body },
    )
    return res.vendor_extraction_guidance
  }

  async updateVendorExtractionGuidanceActive(
    id: string,
    body: { is_active: boolean; row_version: number },
  ): Promise<VendorExtractionGuidance> {
    const res = await this.request<{ vendor_extraction_guidance: VendorExtractionGuidance }>(
      `/vendor-extraction-guidance/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
    return res.vendor_extraction_guidance
  }

  listVehicles(params: ReferenceListParams & { status?: string } = {}): Promise<Page<Vehicle>> {
    return this.request(`/vehicles${query({ ...params })}`)
  }

  async createVehicle(body: Partial<Vehicle> & { number: string }): Promise<Vehicle> {
    const res = await this.request<{ vehicle: Vehicle }>('/vehicles', { method: 'POST', body })
    return res.vehicle
  }

  async updateVehicle(vehicleId: string, body: Partial<Vehicle> & { row_version: number }): Promise<Vehicle> {
    const res = await this.request<{ vehicle: Vehicle }>(`/vehicles/${encodeURIComponent(vehicleId)}`, {
      method: 'PATCH',
      body,
    })
    return res.vehicle
  }

  previewVehicleImport(file: File): Promise<VehicleImportPreview> {
    const form = new FormData()
    form.append('file', file)
    return this.request('/vehicles/import/preview', { method: 'POST', form })
  }

  commitVehicleImport(previewToken: string, idempotencyKey?: string): Promise<VehicleImportResult> {
    return this.request('/vehicles/import/commit', {
      method: 'POST',
      body: { preview_token: previewToken },
      idempotencyKey: idempotencyKey ?? this.newIdempotencyKey(),
    })
  }

  // --- analytics ----------------------------------------------------------

  private analytics<T>(name: string, params: AnalyticsParams = {}): Promise<T> {
    return this.request<T>(
      `/analytics/${name}${query({
        company_id: params.company_id,
        vendor_id: params.vendor_id,
        entity_type: params.entity_type,
        dimension: params.dimension,
        group_by: params.group_by,
        // THE FILTERS BUTTON'S CRITERIA (owner, 2026-09-14). `query` drops an
        // undefined, an empty string and an empty array, so an unset criterion
        // never reaches the wire and the server's unknown-parameter check stays
        // meaningful.
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
        uploaded_from: params.uploaded_from,
        uploaded_to: params.uploaded_to,
        exported_from: params.exported_from,
        exported_to: params.exported_to,
        total_min: params.total_min,
        total_max: params.total_max,
        code_id: params.code_id,
        department_id: params.department_id,
        account_id: params.account_id,
        flag_type: params.flag_type,
        flag_type_any: params.flag_type_any,
        has_active_hold: params.has_active_hold,
      })}`,
    )
  }

  async getAnalyticsSpendByEntity(params?: AnalyticsParams): Promise<AnalyticsSpendByEntity> {
    const r = await this.analytics<AnalyticsSpendByEntity>('spend-by-entity', params)
    return { ...r, entities: parseTotals(r.entities) }
  }

  getAnalyticsCorrections(params?: AnalyticsParams): Promise<AnalyticsCorrections> {
    return this.analytics('corrections', params)
  }

  async getAnalyticsByDimension(
    dimension: NonNullable<AnalyticsParams['dimension']>,
    params?: AnalyticsParams,
  ): Promise<AnalyticsByDimension> {
    const r = await this.analytics<AnalyticsByDimension>('by-dimension', {
      ...params,
      dimension,
    })
    return { ...r, rows: parseTotals(r.rows) }
  }

  async getAnalyticsSpendByCode(
    groupBy: NonNullable<AnalyticsParams['group_by']>,
    params?: AnalyticsParams,
  ): Promise<AnalyticsSpendByCode> {
    const r = await this.analytics<AnalyticsSpendByCode>('spend-by-code', {
      ...params,
      group_by: groupBy,
    })
    return { ...r, rows: parseTotals(r.rows) }
  }

  /**
   * The KPI block. NO PARAMETERS — the route refuses an unknown one, and this
   * block is deliberately independent of the Filters button, so passing the
   * criteria here would be both a 400 and a lie.
   */
  async getAnalyticsKpis(): Promise<AnalyticsKpis> {
    const r = await this.request<AnalyticsKpis>('/analytics/kpis')
    // `toNumber` answers null for a missing amount, which is right for an
    // invoice's total and wrong for a tile: a tile with nothing in it is zero
    // dollars, not an unknown amount.
    const tile = (t: { count: number; total: unknown }) => ({
      count: t.count,
      total: toNumber(t.total) ?? 0,
    })
    return {
      ...r,
      imported_this_week: tile(r.imported_this_week),
      in_progress: tile(r.in_progress),
      past_due: tile(r.past_due),
      exported_this_week: tile(r.exported_this_week),
      // Same decimal-string-to-number pass, per band.
      outstanding_by_age: parseTotals(r.outstanding_by_age),
      outstanding_unknown_age: tile(r.outstanding_unknown_age),
    }
  }

  // --- reports ------------------------------------------------------------

  createReport(criteria: ReportCriteria): Promise<CreateReportResponse> {
    return this.request('/reports', { method: 'POST', body: criteria })
  }

  async listReports(): Promise<ReportRequestSummary[]> {
    const r = await this.request<{ data: ReportRequestSummary[] }>('/reports')
    return r.data
  }

  // --- ops (Admin → Errors) --------------------------------------------------

  async listOpsErrors(params: {
    window: OpsErrorsWindow
    kinds?: OpsErrorKind[]
    includeWarnings?: boolean
    excludeEvents?: string[]
    q?: string
    queryId?: string
  }): Promise<OpsErrorsResponse> {
    const qs = new URLSearchParams({ window: params.window })
    if (params.kinds !== undefined && params.kinds.length > 0) qs.set('kinds', params.kinds.join(','))
    if (params.includeWarnings) qs.set('include_warnings', 'true')
    if (params.excludeEvents !== undefined && params.excludeEvents.length > 0) {
      qs.set('exclude_events', params.excludeEvents.join(','))
    }
    if (params.q !== undefined && params.q.trim() !== '') qs.set('q', params.q.trim())
    if (params.queryId !== undefined) qs.set('query_id', params.queryId)
    return this.request<OpsErrorsResponse>(`/ops/errors?${qs.toString()}`)
  }

  async listDeployReports(): Promise<DeployReport[]> {
    const r = await this.request<{ data: DeployReport[] }>('/ops/deploys')
    return r.data
  }

  getReportContents(reportId: string): Promise<ReportContentsResponse> {
    return this.request(`/reports/${encodeURIComponent(reportId)}`)
  }

  // --- corrections --------------------------------------------------------


  // --- directory ----------------------------------------------------------

  /** Microsoft Graph, on the signed-in user's delegated token (§0.11). The
      matcher stores a staff tag as the user principal name, so that is the id. */
  async searchDirectoryUsers(term: string, top = 25): Promise<DirectoryUser[]> {
    const trimmed = term.trim()
    const filter = trimmed
      ? `startswith(displayName,'${trimmed.replace(/'/g, "''")}') or startswith(userPrincipalName,'${trimmed.replace(/'/g, "''")}')`
      : undefined
    const url = `https://graph.microsoft.com/v1.0/users${query({
      $select: 'id,displayName,userPrincipalName,mail,jobTitle,department',
      $top: top,
      $filter: filter,
    })}`
    const res = await this.request<{
      value: {
        id: string
        displayName: string
        userPrincipalName: string
        mail?: string | null
        jobTitle?: string | null
        department?: string | null
      }[]
    }>('', { absolute: url, graph: true })
    return (res.value ?? []).map((u) => ({
      id: u.id,
      display_name: u.displayName,
      upn: u.userPrincipalName,
      mail: u.mail ?? null,
      job_title: u.jobTitle ?? null,
      department: u.department ?? null,
    }))
  }
}
